import {
  DeployContractPackageResult,
  FTBalanceTokenResult,
} from '@block-explorer/entities';
import { deriveFtTokenData, FtTokenData } from './derive-ft-token-data-service';
import {
  ftTokenAmountToCurrency,
  getAmountFromMotes,
} from '../utils/ft-token-amount';
import Big from 'big.js';

export enum BalanceType {
  TOKENS_MARKET_DATA_BALANCE = 'tokens_market_data_balance',
  TOKENS_BALANCE = 'tokens_balance',
  TOKENS_MIXED_BALANCE = 'tokens_mixed_balance',
}

export interface TokensBalanceSummary {
  owner_type: string;
  owner_hash: string;
  tokenAmount: string;
  tokenBalanceInCurrency: string;
  symbol: string | null | undefined;
  contractPackage: DeployContractPackageResult | null | undefined;
  contractTypeId: number | null | undefined;
  contractPackageHash: string | null | undefined;
  iconUrl: string | null | undefined;
  name: string | null | undefined;
  tokenBalance: string;
  cep18AmountData: FtTokenData;
}

const ZERO_BALANCE = '0';

const sortByBalanceDescending = (
  tokens: TokensBalanceSummary[],
  balanceKeySort: keyof Pick<
    TokensBalanceSummary,
    'tokenAmount' | 'tokenBalanceInCurrency'
  >
): TokensBalanceSummary[] => {
  return [...tokens].sort((a, b) =>
    Big(a[balanceKeySort]).gt(Big(b[balanceKeySort])) ? -1 : 1
  );
};

const hasMarketValue = (tokenBalanceInCurrency: string): boolean => {
  return tokenBalanceInCurrency
    ? Big(tokenBalanceInCurrency).gt(ZERO_BALANCE)
    : false;
};

const hasTokenBalance = (tokenAmount: string): boolean => {
  return tokenAmount ? Big(tokenAmount).gt(ZERO_BALANCE) : false;
};

const calculateTotalBalance = (tokens: TokensBalanceSummary[]): string => {
  return tokens.reduce(
    (total, token) =>
      Big(total).add(Big(token.tokenBalanceInCurrency)).toString(),
    ZERO_BALANCE
  );
};

const partitionTokensByMarketData = (
  tokens: TokensBalanceSummary[],
  allowZeroBalance: boolean = false
) => {
  const withMarketData = tokens.filter((token) =>
    hasMarketValue(token.tokenBalanceInCurrency)
  );

  const withoutMarketData = tokens.filter((token) => {
    if (allowZeroBalance) {
      return !hasMarketValue(token.tokenBalanceInCurrency);
    }

    return (
      hasTokenBalance(token.tokenAmount) &&
      !hasMarketValue(token.tokenBalanceInCurrency)
    );
  });

  return {
    withMarketData: sortByBalanceDescending(
      withMarketData,
      'tokenBalanceInCurrency'
    ),
    withoutMarketData: sortByBalanceDescending(
      withoutMarketData,
      'tokenAmount'
    ),
  };
};

const computeTokenBalances = (tokens: FTBalanceTokenResult[] | null) => {
  return tokens
    ? tokens.reduce((acc, token) => {
        const cep18AmountData = deriveFtTokenData(
          token?.contract_package
            ? { contractPackageInfo: token.contract_package }
            : null
        );

        const calculatedTokenBalance = getAmountFromMotes(
          token.balance,
          cep18AmountData.decimals
        );

        const tokenBalanceInCurrency =
          calculatedTokenBalance && cep18AmountData.price
            ? ftTokenAmountToCurrency(
                calculatedTokenBalance,
                cep18AmountData.price
              )
            : '0';

        const summary: TokensBalanceSummary = {
          owner_type: token.owner_type,
          owner_hash: token.owner_hash,
          tokenAmount: calculatedTokenBalance,
          tokenBalance: token.balance,
          tokenBalanceInCurrency: tokenBalanceInCurrency,
          symbol: cep18AmountData.symbol,
          contractPackage: token.contract_package,
          contractTypeId: token.contract_package?.contract_type_id,
          contractPackageHash: token.contract_package?.contract_package_hash,
          iconUrl: token.contract_package?.icon_url,
          name:
            token.contract_package?.name ||
            token.contract_package?.contract_name,
          cep18AmountData: cep18AmountData,
        };

        acc.push(summary);

        return acc;
      }, [] as TokensBalanceSummary[])
    : [];
};

export const sortTokensByBalances = (tokens: FTBalanceTokenResult[] | null) => {
  const tokensInfoWithBalance: TokensBalanceSummary[] =
    computeTokenBalances(tokens);

  const { withMarketData, withoutMarketData } = partitionTokensByMarketData(
    tokensInfoWithBalance,
    true
  );

  return [...withMarketData, ...withoutMarketData];
};

export const computeAndSortTokensByBalanceSummary = (
  tokens: FTBalanceTokenResult[] | null
) => {
  const tokensInfoWithBalance = computeTokenBalances(tokens);

  const { withMarketData, withoutMarketData } = partitionTokensByMarketData(
    tokensInfoWithBalance
  );

  if (withMarketData.length > 0 && withoutMarketData.length === 0) {
    return {
      type: BalanceType.TOKENS_MARKET_DATA_BALANCE,
      balance: calculateTotalBalance(withMarketData),
      tokens: withMarketData,
    };
  }

  if (withMarketData.length === 0 && withoutMarketData.length > 0) {
    return {
      type: BalanceType.TOKENS_BALANCE,
      balance: ZERO_BALANCE,
      tokens: withoutMarketData,
    };
  }

  return {
    type: BalanceType.TOKENS_MIXED_BALANCE,
    balance: calculateTotalBalance(withMarketData),
    tokens: [...withMarketData, ...withoutMarketData],
  };
};
